import React from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';

// style
import { BoxProps, Div } from '../box';
import { spacing } from '../../style';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';
import { variants } from './style';

interface CoverPhotoProps {
  className?: string;
  variant?: keyof typeof variants;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  padding?: keyof typeof spacing;
  borderRadius?: 'sm' | 'md';
  photo?: string;
  componentClass?: React.ElementType;
  borderRadiusTop?: 'sm' | 'md';
  'data-name'?: string;
  'data-testid'?: string;
  backgroundPositionY?: 'center';
  height?: CssUnitsAsResponsiveObject<string | number>;
  backgroundColor?: string;
  backgroundRepeat?: 'no-repeat';
  backgroundSize?: CssUnitsAsResponsiveObject<React.CSSProperties['backgroundSize']>;
  backgroundPosition?: string;
  minHeight?: number | string;

  flexShrink?: React.CSSProperties['flexShrink'];

  maxWidth?: BoxProps['maxWidth'];
  width?: BoxProps['width'];
}

const CoverPhotoComponent = ({
  className,
  variant,
  style,
  children,
  padding,
  borderRadius,
  photo,
  componentClass: Component = Div,
  borderRadiusTop,
  'data-name': dataName,
  'data-testid': dataTestId,
  width,
  maxWidth,
}: CoverPhotoProps) => (
  <Component
    padding={padding}
    data-name={dataName}
    data-value={photo}
    maxWidth={maxWidth}
    width={width}
    data-testid={dataTestId}
    borderRadius={borderRadius}
    borderRadiusTop={borderRadiusTop}
    className={classNames('box-cover-photo', `variant-${variant}`, className)}
    style={{
      ...style,
      backgroundImage: photo ? `url("${photo}")` : '',
    }}
  >
    {children}
  </Component>
);

const CoverPhoto = styled(React.memo(CoverPhotoComponent))<CoverPhotoProps>`
  position: relative;
  ${props => variants[props.variant || 'gray100']};
  background-position-y: ${props => props.backgroundPositionY};
  background-position: ${props => props.backgroundPosition || 'center'};
  background-repeat: ${props => props.backgroundRepeat || 'no-repeat'};
  background-color: ${props => props.backgroundColor};
  flex-shrink: ${props => props.flexShrink};
  ${props =>
    props.minHeight &&
    css`
      min-height: ${typeof props.height === 'number' ? `${props.minHeight}px` : props.minHeight};
    `};
  ${props =>
    getCssRuleWithResponsiveCalculation<CoverPhotoProps['height']>({
      property: 'height',
      formatValue: value => {
        if (typeof value === 'number') {
          return `${value}px`;
        }

        return value;
      },
      value: props.height,
    })};

  ${props =>
    getCssRuleWithResponsiveCalculation<CoverPhotoProps['backgroundSize']>({
      property: 'background-size',
      value: props.backgroundSize || 'cover',
    })};
`;

export default React.memo(CoverPhoto);
